<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextDialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import types from 'dashboard/store/mutation-types';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
    NextDialog,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      crmHandoffActions: [],
      crmHandoffActionInProgress: null,
      pendingCrmHandoffAction: null,
      buyerLeadDialogCancelLabel: 'Cancelar',
      buyerLeadDialogConfirmLabel: 'Continuar',
      handoffWithoutEmailDialogDescription:
        'El contacto no tiene correo registrado. Desea continuar y generar el lead solo con el telefono?',
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    selectedProperty() {
      return (
        this.currentChat?.custom_attributes?.selected_property ||
        this.currentChat?.customAttributes?.selected_property ||
        null
      );
    },
    handoffContact() {
      const sender = this.currentChat?.meta?.sender || {};
      const contactId = sender.id;
      const storeContact = contactId
        ? this.$store.getters['contacts/getContact'](contactId)
        : null;

      return storeContact || sender;
    },
    handoffContactEmail() {
      return String(this.handoffContact?.email || '').trim();
    },
    handoffContactPhone() {
      return String(this.handoffContact?.phone_number || '').trim();
    },
    handoffWithoutEmailDialogTitle() {
      return this.pendingCrmHandoffAction?.label || 'Generar Lead';
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.fetchCrmHandoffActions();
      },
    },
  },
  methods: {
    async fetchCrmHandoffActions() {
      if (!this.conversationId) return;

      try {
        const response = await ConversationApi.crmHandoffOptions({
          conversationId: this.conversationId,
        });
        this.crmHandoffActions = response.data.actions || [];
      } catch {
        this.crmHandoffActions = [];
      }
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },

    shouldValidateHandoffContact(handoffType) {
      return ['buyer', 'owner', 'agent', 'commercial_appraisal'].includes(
        handoffType
      );
    },

    validateHandoffContact(action) {
      if (!this.shouldValidateHandoffContact(action.type)) return true;

      if (!this.handoffContactPhone) {
        useAlert(
          `El telefono del contacto es obligatorio para ${action.label}.`
        );
        return false;
      }

      if (!this.handoffContactEmail) {
        this.pendingCrmHandoffAction = action;
        this.$refs.buyerLeadWithoutEmailDialog.open();
        return false;
      }

      return true;
    },

    validateBuyerProperty(action) {
      if (action.type !== 'buyer') return true;

      if (this.selectedProperty) return true;

      useAlert(
        'Debe seleccionar una propiedad o inmueble para generar un Lead Comprador.'
      );
      return false;
    },

    requestCrmHandoff(action) {
      if (!this.validateBuyerProperty(action)) return;
      if (!this.validateHandoffContact(action)) return;

      this.continueCrmHandoff(action);
    },

    continueCrmHandoff(action) {
      this.onCrmHandoff(action);
    },

    closeBuyerLeadWithoutEmailDialog() {
      this.pendingCrmHandoffAction = null;
      this.$refs.buyerLeadWithoutEmailDialog.close();
    },

    confirmBuyerLeadWithoutEmail() {
      const action = this.pendingCrmHandoffAction;
      this.pendingCrmHandoffAction = null;
      this.$refs.buyerLeadWithoutEmailDialog.close();

      if (action) this.continueCrmHandoff(action);
    },

    async onCrmHandoff(action) {
      this.crmHandoffActionInProgress = action.type;
      try {
        const response = await ConversationApi.crmHandoff({
          conversationId: this.currentChat.id,
          handoffType: action.type,
        });
        this.$store.commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId: this.currentChat.id,
          customAttributes: response.data.custom_attributes,
        });
        useAlert(`${action.label} enviado correctamente`);
      } catch (error) {
        const message =
          error?.response?.data?.error || `No se pudo ejecutar ${action.label}`;
        useAlert(message);
      } finally {
        this.crmHandoffActionInProgress = null;
      }
    },
  },
};
</script>

<template>
  <div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div v-if="crmHandoffActions.length" class="mt-3 space-y-2">
      <NextButton
        v-for="action in crmHandoffActions"
        :key="action.type"
        sm
        slate
        class="w-full justify-center"
        icon="i-lucide-send"
        :label="action.label"
        :is-loading="crmHandoffActionInProgress === action.type"
        :disabled="!!crmHandoffActionInProgress"
        @click="requestCrmHandoff(action)"
      />
    </div>
    <NextDialog
      ref="buyerLeadWithoutEmailDialog"
      type="edit"
      width="md"
      :title="handoffWithoutEmailDialogTitle"
      :description="handoffWithoutEmailDialogDescription"
      :cancel-button-label="buyerLeadDialogCancelLabel"
      :confirm-button-label="buyerLeadDialogConfirmLabel"
      @confirm="confirmBuyerLeadWithoutEmail"
      @close="pendingCrmHandoffAction = null"
    />
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>
